import { addItem } from '../../game/inventory';
import type { AccountSave, MailItem } from '../../game/account';
import { ITEMS } from '../../game/items';
import type { Progression } from '../../game/Progression';
import { roomFor } from './bag';

/**
 * Mailbox: market proceeds, bag overflow and system gifts land in
 * `account.mail`. Claiming moves currency into the wallet and items into the
 * active character's bag (whatever does not fit stays in the letter).
 */

export interface ClaimResult {
  items: { id: string; count: number }[];
  silver: number;
  gold: number;
  /** False when some items stayed behind because the bag was full. */
  complete: boolean;
}

export const hasAttachments = (m: MailItem): boolean =>
  m.silver > 0 || m.gold > 0 || m.items.some((i) => i.count > 0) || (m.equip?.length ?? 0) > 0;

export const unreadCount = (a: AccountSave): number => a.mail.filter((m) => !m.read).length;

export function markRead(a: AccountSave, id: string): void {
  const m = a.mail.find((x) => x.id === id);
  if (m) m.read = true;
}

export function claimMail(p: Progression, id: string): ClaimResult | null {
  const a = p.account;
  const m = a.mail.find((x) => x.id === id);
  if (!m) return null;
  const out: ClaimResult = { items: [], silver: m.silver, gold: m.gold, complete: true };
  a.wallet.silver += m.silver;
  a.wallet.gold += m.gold;
  m.silver = 0;
  m.gold = 0;
  const left: MailItem['items'] = [];
  for (const it of m.items) {
    if (it.count <= 0) continue;
    const n = ITEMS[it.id] ? Math.min(it.count, roomFor(p.char, it.id)) : 0;
    if (n > 0) {
      p.giveItem(it.id, n);
      out.items.push({ id: it.id, count: n });
    }
    if (it.count > n) left.push({ id: it.id, count: it.count - n });
  }
  m.items = left;
  // Gear waits in the letter until the bag has room.
  const gearLeft = (m.equip ?? []).filter((e) => !addItem(p.char, e));
  if (m.equip) m.equip = gearLeft;
  m.read = true;
  out.complete = left.length === 0 && gearLeft.length === 0;
  if (out.silver || out.gold) p.emit('wallet', { silver: a.wallet.silver, gold: a.wallet.gold });
  return out;
}

/** Claims every letter; returns the combined haul. */
export function claimAll(p: Progression): ClaimResult {
  const total: ClaimResult = { items: [], silver: 0, gold: 0, complete: true };
  for (const m of [...p.account.mail]) {
    if (!hasAttachments(m)) continue;
    const r = claimMail(p, m.id);
    if (!r) continue;
    total.silver += r.silver;
    total.gold += r.gold;
    for (const it of r.items) {
      const cur = total.items.find((x) => x.id === it.id);
      if (cur) cur.count += it.count;
      else total.items.push({ ...it });
    }
    total.complete &&= r.complete;
  }
  return total;
}

/** Deletes a letter (only once its attachments are claimed). */
export function deleteMail(a: AccountSave, id: string): boolean {
  const m = a.mail.find((x) => x.id === id);
  if (!m || hasAttachments(m)) return false;
  a.mail = a.mail.filter((x) => x.id !== id);
  return true;
}

/** Removes every read letter with nothing left to claim. */
export function deleteRead(a: AccountSave): number {
  const before = a.mail.length;
  a.mail = a.mail.filter((m) => !m.read || hasAttachments(m));
  return before - a.mail.length;
}

export function sendMail(
  a: AccountSave,
  from: string,
  subject: string,
  body: string,
  gift: { items?: { id: string; count: number }[]; silver?: number; gold?: number } = {},
  now = Date.now(),
): MailItem {
  const m: MailItem = {
    id: `m_${now.toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`,
    from,
    subject,
    body,
    items: gift.items ?? [],
    silver: gift.silver ?? 0,
    gold: gift.gold ?? 0,
    read: false,
    at: now,
  };
  a.mail.unshift(m);
  return m;
}
